<template>
  <AttrBase
    v-slot="{ hover }"
    :label="label"
    :compact="compact"
    :disabled="disabled"
    :tooltip="tooltip"
    label-draggable
    @label-drag="handleLabelDrag"
    @label-drag-end="handleLabelDragEnd"
  >
    <div
      class="flex items-center relative bg-yellow1-50 _item"
      :class="{
        _border: (hover && !readonly && !disabled) || focused ? true : false
      }"
    >
      <a-input-number
        :value="_value"
        :bordered="false"
        :controls="false"
        class="text-base font-medium transition-all"
        :class="{
          underline: underline,
          _pointerOnReadonly: readonly && pointerOnReadonly
        }"
        :style="{
          width: width + 'px'
        }"
        :min="min"
        :max="max"
        :readonly="readonly"
        :precision="precision"
        :step="inputStep"
        :disabled="disabled"
        @focus="onFocus"
        @blur="onBlur"
        @change="(val:number) => emit('change',val?val:0)"
        @click="readonly && emit('readonly-click')"
        @keydown="handleKeyDown"
      >
        <template #addonAfter>
          <span v-if="addonAfter" class="">{{ addonAfter }}</span>
        </template>
      </a-input-number>

      <template v-if="selection">
        <div
          class="ml-1 z-10"
          @click.stop="
            () => {
              optionRef?.open?.();
            }
          "
        >
          <icon icon="arrow-down.svg" :width="14" :height="14" fill="none" />
        </div>
        <AttrSelectOptions
          ref="optionRef"
          :value="_value"
          :options="options"
          @change="(val: number) => emit('change', val)"
        />
      </template>

      <slot />
    </div>
  </AttrBase>
</template>
<script lang="ts" setup>
import {
  evaluateExpression,
  parseExpression,
  tokenizeExpression
} from 'expression-engine';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  value: {
    type: [Number, String],
    default: undefined
  },
  addonAfter: {
    type: String,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: undefined
  },
  readonly: {
    type: Boolean,
    default: undefined
  },
  pointerOnReadonly: {
    type: Boolean,
    default: undefined
  },
  underline: {
    type: Boolean,
    default: undefined
  },
  precision: {
    type: Number,
    default: undefined
  },
  step: {
    type: Number,
    default: undefined
  },
  min: {
    type: Number,
    default: undefined
  },
  max: {
    type: Number,
    default: undefined
  },
  width: {
    type: Number,
    default: 64
  },
  compact: {
    type: Boolean,
    default: false
  },
  selection: {
    type: Boolean,
    default: false
  },
  options: {
    type: Array as PropType<
      { label: string; value: string | number | boolean }[]
    >,
    default: () => []
  },
  tooltip: {
    type: String,
    default: ''
  }
});

const focused = ref(false);
const onFocus = () => {
  focused.value = true;
};
const onBlur = () => {
  focused.value = false;
};

const _value = ref<number | string>();
watch(
  () => props.value,
  (val) => {
    if (val !== undefined) _value.value = val;
  },
  { immediate: true }
);

const emit = defineEmits<{
  (event: 'change', val: number): void;
  (event: 'readonly-click'): void;
}>();

// select
const optionRef = ref();

const inputStep = ref<undefined | number>(props.step);
function handleKeyDown(e: KeyboardEvent) {
  if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
    // shift + up/down 变化 10
    inputStep.value = 10;
  } else if (inputStep.value !== props.step) {
    inputStep.value = props.step;
  } else if (e.key === 'Enter') {
    const elm = e.currentTarget as HTMLInputElement;
    try {
      const _value = evaluateExpression(
        parseExpression(tokenizeExpression(elm.value)),
        {}
      ) as number;
      if (
        !isNaN(_value) &&
        _value <= (props?.max ?? Infinity) &&
        _value >= (props?.min ?? -Infinity)
      )
        emit('change', _value);
    } catch {
      /* empty */
    }
    elm.blur();
  }
}

// 标签拖拽时改变数值
const labelDragDelta = ref(0);
const handleLabelDrag = (value: { x: number; y: number }) => {
  // 根据鼠标位移增量累加数值 减去上一次更新delta
  const rawValue = value.x + Number(props?.value ?? 0) - labelDragDelta.value;
  labelDragDelta.value = value.x;
  // 按 step 进行对齐，避免出现 30.4 → 显示为 31% 这类情况
  const step = props.step ?? 1;
  const snappedValue = Math.round(rawValue / step) * step;
  const min = props?.min ?? -Infinity;
  const max = props?.max ?? Infinity;
  if (snappedValue <= max && snappedValue >= min) {
    emit('change', snappedValue);
  }
};
// 还原delta
const handleLabelDragEnd = () => (labelDragDelta.value = 0);
</script>

<style lang="less" scoped>
:deep(.ant-input-number-input) {
  text-align: end;
  padding-right: 0px !important;
}
:deep(.ant-input-number-group-addon) {
  padding: 0;
  border: none;
  font-size: 0.875rem;
  padding-left: 2px !important;
}
:deep(.ant-input-number) {
  &:focus,
  &:hover {
    border-color: #838383;
  }
}

.underline :deep(input) {
  text-decoration: underline !important;
}
._pointerOnReadonly :deep(input) {
  cursor: pointer !important;
}
._border {
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: -0.75rem;
    top: 0;
    bottom: 0;
    border: 1px solid #838383;
    // border-radius: 4px;
  }
}
</style>
